import { useState, useEffect } from 'react';
import styles from './Notification.module.css';

export type NotificationType = 'success' | 'error' | 'warning';

export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
}

interface NotificationProps {
  items: NotificationItem[];
  onDismiss: (id: string) => void;
}

export function Notification({ items, onDismiss }: NotificationProps) {
  if (items.length === 0) return null;

  return (
    <div className={styles.stack} role="status" aria-live="polite">
      {items.map((item) => (
        <NotificationBanner key={item.id} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function NotificationBanner({
  item,
  onDismiss,
}: {
  item: NotificationItem;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(item.id), 4500);
    return () => clearTimeout(timer);
  }, [item.id, onDismiss]);

  return (
    <div className={`${styles.banner} ${styles[item.type]}`}>
      <div className={styles.content}>
        <span className={styles.title}>{item.title}</span>
        {item.message && <span className={styles.message}>{item.message}</span>}
      </div>
      <button
        className={styles.dismiss}
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

let _counter = 0;

export function useNotification() {
  const [items, setItems] = useState<NotificationItem[]>([]);

  const dismiss = (id: string) => setItems((prev) => prev.filter((n) => n.id !== id));

  const notify = (type: NotificationType, title: string, message?: string) => {
    const id = String(++_counter);
    const item: NotificationItem = message !== undefined
      ? { id, type, title, message }
      : { id, type, title };
    setItems((prev) => [...prev, item]);
  };

  return { items, dismiss, notify };
}

export default Notification;
